/**
 * AttendanceCalendarCard
 * Reusable monthly attendance calendar for any employee.
 * Props:
 *   userId  — the user's _id string
 *   title   — optional card title (defaults to "Attendance")
 */
import React, { useState } from "react";
import { useGetEmployeeMonthViewQuery } from "../../store/appApi";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const COLOR_MAP = {
  PRESENT:        "#2bc155",
  PARTIAL:        "#f6a821",
  HALF_DAY_LEAVE: "#3f9ae0",
  ON_LEAVE:       "#3f9ae0",
  PENDING:        "#ff9f43",
  ABSENT:         "#f32d2d",
  WEEKEND:        "#94a3b8",
  HOLIDAY:        "#8b5cf6",
  UPCOMING:       "#64748b",
};

const PRESENT_COLOR = "#2bc155";

const TAG_LABEL = {
  PRESENT:        "Present",
  PARTIAL:        "Partial",
  HALF_DAY_LEAVE: "Half Day",
  ON_LEAVE:       "On Leave",
  PENDING:        "Pending",
  ABSENT:         "Absent",
  WEEKEND:        "Weekend",
  HOLIDAY:        "Holiday",
  UPCOMING:       "Upcoming",
};

const STATUS_STAT = [
  { label: "Present",  key: "present",  color: "#2bc155" },
  { label: "Partial",  key: "partial",  color: "#f6a821" },
  { label: "On Leave", key: "onLeave",  color: "#3f9ae0" },
  { label: "Absent",   key: "absent",   color: "#f32d2d" },
  { label: "Holiday",  key: "holiday",  color: "#8b5cf6" },
];

const toMonthStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const SelfieThumb = ({ url, label }) => {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const src = url.startsWith("http") ? url : `${BASE_URL}${url}`;
  return (
    <>
      <img
        src={src} alt={label} onClick={() => setOpen(true)}
        style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 3, cursor: "pointer", border: "1px solid #dee2e6" }}
        title={`Click to enlarge — ${label}`}
      />
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out",
        }}>
          <div style={{ textAlign: "center" }}>
            <img src={src} alt={label} style={{ maxHeight: "80vh", maxWidth: "90vw", borderRadius: 8 }} />
            <div style={{ color: "#fff", marginTop: 8, fontSize: 13 }}>{label}</div>
          </div>
        </div>
      )}
    </>
  );
};

const AttendanceCalendarCard = ({ userId, title = "Attendance" }) => {
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const month = toMonthStr(monthDate);
  const { data, isLoading } = useGetEmployeeMonthViewQuery(
    { id: userId, month },
    { skip: !userId },
  );

  const days    = data?.days    || [];
  const summary = data?.summary || {};

  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).getDay();
  const blanks   = Array(firstDay).fill(null);
  const sel      = selectedDay && days.find((d) => d.date === selectedDay);

  const prevMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() - 1);
    setMonthDate(d);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + 1);
    setMonthDate(d);
    setSelectedDay(null);
  };

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">{title}</h5>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={prevMonth}>&lsaquo;</button>
          <span className="fw-semibold" style={{ minWidth: 130, textAlign: "center" }}>
            {monthDate.toLocaleString("default", { month: "long", year: "numeric" })}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={nextMonth}
            disabled={toMonthStr(monthDate) >= toMonthStr(new Date())}
          >&rsaquo;</button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="d-flex border-bottom">
        {STATUS_STAT.map((s) => (
          <div key={s.key} className="flex-fill text-center py-2" style={{ borderLeft: `3px solid ${s.color}` }}>
            <div className="fw-bold fs-5">{summary[s.key] ?? 0}</div>
            <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card-body p-2">
        {isLoading ? (
          <div className="text-center py-4">Loading…</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="text-center fw-bold text-muted py-1" style={{ fontSize: 10 }}>{d}</div>
            ))}
            {blanks.map((_, i) => <div key={`b${i}`} />)}
            {days.map((day) => {
              const isHalfDay = day.tag === "HALF_DAY_LEAVE";
              const color = COLOR_MAP[day.tag] || "#ccc";
              return (
                <div
                  key={day.date}
                  onClick={() => setSelectedDay(day.date === selectedDay ? null : day.date)}
                  style={{
                    background: isHalfDay
                      ? `linear-gradient(135deg,${color}33 50%,${PRESENT_COLOR}33 50%)`
                      : `${color}22`,
                    borderLeft: `3px solid ${color}`,
                    borderRight: isHalfDay ? `3px solid ${PRESENT_COLOR}` : undefined,
                    borderRadius: 4,
                    padding: "4px 3px",
                    cursor: "pointer",
                    outline: selectedDay === day.date ? `2px solid ${color}` : "none",
                    minHeight: 44,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{parseInt(day.date.split("-")[2])}</div>
                  <div style={{ fontSize: 8, fontWeight: 600, color, textAlign: "center", lineHeight: 1.1 }}>
                    {day.holiday || TAG_LABEL[day.tag]}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Day detail panel */}
      {sel && (
        <div className="card-footer" style={{ background: "#f8f9fa" }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-semibold">{sel.date}</span>
            <div className="d-flex align-items-center gap-2">
              <span className="badge" style={{ background: COLOR_MAP[sel.tag] }}>
                {sel.holiday ? `Holiday — ${sel.holiday}` : TAG_LABEL[sel.tag]}
              </span>
              <button className="btn btn-xs btn-outline-secondary py-0 px-1" onClick={() => setSelectedDay(null)}>✕</button>
            </div>
          </div>
          <div className="row g-2" style={{ fontSize: 13 }}>
            {sel.punchIn && (
              <div className="col-6">
                <span className="text-muted">Punch In: </span>
                <strong>{new Date(sel.punchIn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</strong>
              </div>
            )}
            {sel.punchOut && (
              <div className="col-6">
                <span className="text-muted">Punch Out: </span>
                <strong>{new Date(sel.punchOut).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</strong>
              </div>
            )}
            {sel.workDurationMinutes != null && (
              <div className="col-6">
                <span className="text-muted">Hours: </span>
                <strong>{(sel.workDurationMinutes / 60).toFixed(1)}h</strong>
              </div>
            )}
            {sel.distanceFromSite != null && (
              <div className="col-6">
                <span className="text-muted">Distance: </span>
                <strong>{sel.distanceFromSite}m</strong>
              </div>
            )}
            {sel.leave && (
              <div className="col-12">
                <span className="text-muted">Leave: </span>
                <strong>{sel.leave.leaveType}</strong>
                <span className={`badge ms-1 bg-${sel.leave.status === "approved" ? "success" : "warning"}`}>{sel.leave.status}</span>
              </div>
            )}
          </div>
          {/* Selfies */}
          {(sel.selfieUrl || sel.punchOutSelfieUrl) && (
            <div className="d-flex gap-3 mt-2">
              {sel.selfieUrl && (
                <div className="text-center">
                  <SelfieThumb url={sel.selfieUrl} label="Punch-In Selfie" />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>In</div>
                </div>
              )}
              {sel.punchOutSelfieUrl && (
                <div className="text-center">
                  <SelfieThumb url={sel.punchOutSelfieUrl} label="Punch-Out Selfie" />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>Out</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="card-footer d-flex flex-wrap gap-3 py-2" style={{ fontSize: 10 }}>
        {Object.entries(TAG_LABEL).filter(([t]) => t !== "UPCOMING").map(([tag, label]) => (
          <span key={tag} className="d-flex align-items-center gap-1">
            {tag === "HALF_DAY_LEAVE" ? (
              <span style={{ width: 9, height: 9, borderRadius: 2, background: `linear-gradient(135deg,${COLOR_MAP[tag]} 50%,${PRESENT_COLOR} 50%)`, display: "inline-block" }} />
            ) : (
              <span style={{ width: 9, height: 9, borderRadius: 2, background: COLOR_MAP[tag], display: "inline-block" }} />
            )}
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AttendanceCalendarCard;
